import React, { FC, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSignupWizard } from './signup-wizard-context';
import WizardStep from './wizard-step';
import { useStepNavigation } from './use-step-navigation';
import { isValidEmail } from '../../util';
import Field from '../../components/shared/Field/Field';
import StyledCheckbox from '../../components/shared/StyledCheckbox/StyledCheckbox';
import Spinner from '../../components/shared/Spinner/Spinner';
import TooltipContainer from '../../components/shared/TooltipContainer/TooltipContainer';
import { ServiceSupplierSignup } from '../../services/service-supplier-signup';
import { ReactComponent as SuccessIcon } from '../../assets/svg/successIcon.svg';
import { ReactComponent as InfoIconLight } from '../../assets/svg/InfoIconLight.svg';
import styles from './step-about-you.module.scss';

// Orange spinner (design system default is gray) via the Spinner's CSS var.
const spinnerStyle = { '--spinner-circle-color': 'var(--orange-500)' } as React.CSSProperties;

type EmailStatus = 'idle' | 'checking' | 'available' | 'registered';

const StepAboutYou: FC = () => {
  const { t } = useTranslation('newSupplier', { keyPrefix: 'wizard' });
  const { state } = useSignupWizard();

  const [name, setName] = useState(state.name || '');
  const [title, setTitle] = useState(state.title || '');
  const [phoneNumber, setPhoneNumber] = useState(state.phoneNumber || '');
  const [email, setEmail] = useState(state.email || '');
  const [password, setPassword] = useState(state.password || '');
  const [confirm, setConfirm] = useState('');
  const [addAsContact, setAddAsContact] = useState(state.addAsContact ?? true);

  // Restore the prior check result when navigating back to this step.
  const [emailStatus, setEmailStatus] = useState<EmailStatus>(
    state.email && state.emailExists != null
      ? (state.emailExists ? 'registered' : 'available')
      : 'idle'
  );
  const [errors, setErrors] = useState<Record<string, string>>({});

  // The most recent email a check was started for, so a slow response for a
  // stale value can't overwrite the status of what's now in the field.
  const latestCheckRef = useRef('');

  const isRegistered = emailStatus === 'registered';
  const isAvailable = emailStatus === 'available';
  const showPassword = isAvailable;
  const passwordTooShort = password.length > 0 && password.length < 8;
  const passwordsMatch = password.length >= 8 && password === confirm;
  const confirmMismatch = confirm.length > 0 && password !== confirm;

  const runEmailCheck = async (value: string) => {
    const v = value.trim().toLowerCase();
    latestCheckRef.current = v;
    if (!v) { setEmailStatus('idle'); return; }
    if (!isValidEmail(v)) {
      setEmailStatus('idle');
      setErrors(prev => ({ ...prev, email: t('aboutYou.errors.emailInvalid', 'Ange en giltig e-postadress') }));
      return;
    }
    setErrors(prev => ({ ...prev, email: '' }));
    setEmailStatus('checking');
    try {
      const { exists } = await ServiceSupplierSignup.checkEmail(v);
      if (latestCheckRef.current !== v) return; // superseded by a newer check
      setEmailStatus(exists ? 'registered' : 'available');
    } catch {
      if (latestCheckRef.current !== v) return;
      setEmailStatus('idle');
      setErrors(prev => ({ ...prev, email: t('aboutYou.errors.emailCheckFailed', 'Kunde inte verifiera e-postadressen. Försök igen.') }));
    }
  };

  // A prefilled email is never blurred, so run the account check for it once on
  // mount — the step can't proceed until we know whether it already exists.
  const prefillCheckDone = useRef(false);
  useEffect(() => {
    if (prefillCheckDone.current) return;
    prefillCheckDone.current = true;
    if (email && emailStatus === 'idle') runEmailCheck(email);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onEmailChange = (val: string) => {
    setEmail(val);
    // Any edit invalidates a prior result — hide password fields until re-checked.
    if (emailStatus === 'available' || emailStatus === 'registered') setEmailStatus('idle');
    if (errors.email) setErrors(prev => ({ ...prev, email: '' }));
  };

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!name.trim()) newErrors.name = t('aboutYou.errors.nameRequired', 'Namn krävs');
    if (!email.trim()) newErrors.email = t('aboutYou.errors.emailRequired', 'E-post krävs');
    else if (!isValidEmail(email.trim())) newErrors.email = t('aboutYou.errors.emailInvalid', 'Ange en giltig e-postadress');

    if (isAvailable) {
      if (password.length < 8) newErrors.password = t('aboutYou.errors.passwordMinLength', 'Lösenordet måste vara minst 8 tecken');
      if (password !== confirm) newErrors.confirm = t('aboutYou.errors.passwordsDontMatch', 'Lösenorden matchar inte');
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const canProceed =
    name.trim().length > 0 &&
    ((isAvailable && passwordsMatch) || isRegistered);

  const { handleNext, handleBack } = useStepNavigation({
    currentStep: 4,
    nextPath: '/supplier-sign-up/invite',
    prevPath: '/supplier-sign-up/places',
    validate,
    eventName: 'signup_wizard_step_about_you',
    onBeforeNext: () => ({
      name: name.trim(),
      title: title.trim(),
      phoneNumber: phoneNumber.trim(),
      email: email.trim().toLowerCase(),
      password: isAvailable ? password : '',
      addAsContact,
      emailExists: isRegistered,
    }),
  });

  return (
    <WizardStep
      step={4}
      title={t('aboutYou.title', 'Vem är du?')}
      description={t('aboutYou.description', 'Dina uppgifter')}
      onNext={handleNext}
      onBack={handleBack}
      canProceed={canProceed}
    >
      {/* Email — first field; runs the account check on blur */}
      <div style={{ position: 'relative' }}>
        <Field
          id="email"
          type="email"
          value={email}
          onChange={onEmailChange}
          onBlur={(e) => runEmailCheck(e.target.value)}
          placeholder="namn@företag.se"
          label={t('aboutYou.email', 'E-post')}
          required
          errorMessage={errors.email}
          style={{ paddingRight: '2.25rem' }}
          statusSlot={
            emailStatus === 'checking' ? <Spinner progress={0} size="xxs" style={spinnerStyle} />
              : isAvailable ? <SuccessIcon />
                : isRegistered ? (
                  <TooltipContainer
                    renderReferenceComponent={(className, ref) => (
                      <span ref={ref} className={className} style={{ display: 'inline-flex', cursor: 'help' }}>
                        <InfoIconLight style={{ width: '14px', height: '14px' }} />
                      </span>
                    )}
                  >
                    <div style={{ fontSize: '0.75rem', maxWidth: '240px' }}>
                      {t('aboutYou.emailRegisteredInfo', 'Den här e-postadressen har redan ett konto i Aceve. Vi kopplar det kontot till ditt leverantörskonto när du har slutfört registreringen.')}
                    </div>
                  </TooltipContainer>
                )
                  : undefined
          }
        />
        {emailStatus === 'checking' && (
          <p className={styles.helperFloating} style={{ color: 'var(--gray-500)' }}>
            {t('aboutYou.emailChecking', 'Kontrollerar om e-postadressen redan används…')}
          </p>
        )}
      </div>

      {/* Name */}
      <Field
        id="name"
        value={name}
        onChange={(val) => { setName(val); if (errors.name) setErrors(prev => ({ ...prev, name: '' })); }}
        placeholder="Förnamn Efternamn"
        label={t('aboutYou.name', 'Namn')}
        required
        errorMessage={errors.name}
      />

      {/* Title */}
      <Field
        id="title"
        value={title}
        onChange={setTitle}
        placeholder={t('aboutYou.titlePlaceholder', 't.ex. Projektledare')}
        label={t('aboutYou.titleLabel', 'Titel')}
      />

      {/* Phone Number */}
      <Field
        id="phone-number"
        type="tel"
        value={phoneNumber}
        onChange={setPhoneNumber}
        placeholder="+46 70 000 00 00"
        label={t('aboutYou.phone', 'Telefonnummer')}
      />

      {/* Password + Confirm — only revealed for a new (available) email.
          A registered email continues with the user's existing credentials. */}
      {showPassword && (
        <div className={styles.reveal}>
          <div style={{ position: 'relative' }}>
            <Field
              id="password"
              type="password"
              value={password}
              onChange={(val) => { setPassword(val); if (errors.password) setErrors(prev => ({ ...prev, password: '' })); }}
              placeholder={t('aboutYou.passwordPlaceholder', 'Minst 8 tecken')}
              label={t('aboutYou.password', 'Lösenord')}
              required
              errorMessage={errors.password}
            />
            {passwordTooShort && <p className={styles.helperFloating} style={{ color: 'var(--red-700)' }}>
              {t('aboutYou.passwordHint', 'Lösenordet måste vara minst 8 tecken')}
            </p>}
          </div>
          <div style={{ position: 'relative' }}>
            <Field
              id="confirm-password"
              type="password"
              value={confirm}
              onChange={(val) => { setConfirm(val); if (errors.confirm) setErrors(prev => ({ ...prev, confirm: '' })); }}
              placeholder={t('aboutYou.confirmPlaceholder', 'Upprepa lösenordet')}
              label={t('aboutYou.confirmPassword', 'Bekräfta lösenord')}
              required
              errorMessage={errors.confirm}
            />
            {confirmMismatch && (
              <p className={styles.helperFloating} style={{ color: 'var(--red-700)' }}>
                {t('aboutYou.errors.passwordsDontMatch', 'Lösenorden matchar inte')}
              </p>
            )}
          </div>
        </div>
      )}

      {/* Contact person checkbox */}
      <label className="flex items-center gap-3 cursor-pointer select-none tracking-normal">
        <StyledCheckbox checked={addAsContact} onChange={() => setAddAsContact(!addAsContact)} />
        <span style={{ fontSize: '0.875rem', color: 'var(--gray-700)' }}>
          {t('aboutYou.addAsContact', 'Visa mig som kontaktperson i företagspresentationen')}
        </span>
      </label>
    </WizardStep>
  );
};

export default StepAboutYou;
